import type { FC, ReactNode } from 'react'
import type { NavLink } from '@/types'

import Image from 'next/image'
import { useRef } from 'react'

import { cn } from '@/lib/utils'
import { MediaFrame } from '@/components/generals/media-frame'
import { MoreLink } from '@/components/generals/more-link'
import { SectionLabel } from '@/components/generals/section-label'
import { StatValue } from '@/components/generals/stat-value'

import {
    FULL_MOTION,
    gsap,
    useGSAP
} from '@/lib/gsap'

import { media } from '@/constants/media'
import { natureFeatures, natureStats } from '@/constants/experience'

interface NatureSectionProps {
    index?: string
    more?: NavLink
}

export const NatureSection: FC<NatureSectionProps> = ({ index = '09', more }): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)
    const pinRef = useRef<HTMLDivElement>(null)

    useGSAP(() => {
        const section = sectionRef.current
        if (!section) return

        const mm = gsap.matchMedia()

        // The camera pulls back to the full vista while the title rises.
        mm.add(FULL_MOTION, () => {
            const image = section.querySelector('[data-nature-image]')
            const title = section.querySelector('[data-nature-title]')
            const copy = section.querySelectorAll('[data-nature-copy]')

            gsap.timeline({
                scrollTrigger: {
                    trigger: section,
                    start: 'top top',
                    end: '+=110%',
                    pin: pinRef.current,
                    scrub: 0.6
                }
            })
                .fromTo(image, { scale: 1.38 }, { scale: 1, ease: 'none', duration: 1 }, 0)
                .fromTo(title, { clipPath: 'inset(-20% 0% 120% 0%)', yPercent: 30 }, {
                    clipPath: 'inset(-20% 0% -20% 0%)',
                    yPercent: 0,
                    ease: 'power2.out',
                    duration: 0.45
                }, 0.12)
                .fromTo(copy, { autoAlpha: 0, y: 40 }, {
                    autoAlpha: 1,
                    y: 0,
                    ease: 'power2.out',
                    duration: 0.3,
                    stagger: 0.08
                }, 0.5)
        })

        return () => mm.revert()
    }, { scope: sectionRef })

    return (
        <section
            ref={sectionRef}
            id='nature'
            aria-labelledby='nature-heading'
            className='relative z-10 bg-bone text-charcoal'
        >
            <div ref={pinRef} className='relative h-[100svh] min-h-[560px] overflow-hidden bg-pine'>
                <div data-nature-image className='absolute inset-0'>
                    <Image
                        src={media.natureHero.src}
                        alt={media.natureHero.alt}
                        fill
                        sizes='100vw'
                        className='object-cover'
                        style={{ objectPosition: media.natureHero.position }}
                    />
                </div>

                <div aria-hidden='true' className='absolute inset-0 bg-gradient-to-t from-pine/90 via-pine/25 to-pine/10' />

                <div className='relative z-10 flex h-full flex-col justify-end px-5 pb-12 text-bone sm:px-8 lg:px-12 lg:pb-16'>
                    <SectionLabel index={index} className='text-bone/80'>
                        Lifestyle & nature
                    </SectionLabel>

                    <h2
                        id='nature-heading'
                        data-nature-title
                        className='mt-6 max-w-5xl font-serif text-[clamp(2.8rem,7.4vw,8rem)] leading-[0.95] tracking-[-0.01em]'
                    >
                        Golf is only <em>half the day.</em>
                    </h2>

                    <div className='mt-8 grid gap-6 lg:grid-cols-12 lg:items-end'>
                        <p data-nature-copy className='max-w-xl text-base leading-relaxed text-bone/85 lg:col-span-6'>
                            Swim in the reservoir before breakfast, run thirty kilometres of trail through the pines, or take the boardwalk down to a beach you&apos;ll probably have to yourself. The estate is as much nature reserve as golf course.
                        </p>

                        <p data-nature-copy className='font-mono text-[11px] uppercase tracking-[0.24em] text-bone/70 lg:col-span-4 lg:col-start-9 lg:text-right'>
                            Protected dune habitat · Since 2021
                        </p>
                    </div>
                </div>
            </div>

            <div className='px-5 py-24 sm:px-8 lg:px-12 lg:py-32'>
                <div className='grid gap-12 md:grid-cols-3 md:gap-6 lg:gap-8'>
                    {natureFeatures.map((feature, index) => (
                        <article key={feature.title} className={cn(index === 1 && 'md:mt-24', index === 2 && 'md:mt-10')}>
                            <MediaFrame
                                image={feature.image}
                                sizes='(min-width: 768px) 32vw, 100vw'
                                mask='up'
                                parallax={8}
                                className='aspect-[4/5] rounded-[4px]'
                            />

                            <h3 data-reveal className='type-wide mt-6 text-xl font-medium uppercase'>
                                {feature.title}
                            </h3>

                            <p data-reveal className='mt-3 max-w-sm text-[15px] leading-relaxed text-charcoal/70'>
                                {feature.description}
                            </p>
                        </article>
                    ))}
                </div>

                <dl className='mt-20 grid grid-cols-2 gap-x-6 gap-y-10 border-t border-charcoal/15 pt-10 lg:mt-28 lg:grid-cols-4'>
                    {natureStats.map((stat) => (
                        <div key={stat.label} data-reveal className='flex flex-col-reverse'>
                            <dt className='mt-2 max-w-[14rem] text-sm leading-snug text-charcoal/65'>
                                {stat.label}
                            </dt>

                            <dd className='type-wide text-[clamp(2rem,3.6vw,3.4rem)] font-light leading-none tracking-[-0.03em]'>
                                <StatValue stat={stat} />
                            </dd>
                        </div>
                    ))}
                </dl>

                {more && (
                    <div data-reveal className='mt-14'>
                        <MoreLink href={more.href} label={more.label} />
                    </div>
                )}
            </div>
        </section>
    )
}
